import {ChangeEvent, FunctionComponent, useState} from 'react';
import {logger} from '@/initializers/logger';

interface ExtractedDetails {
  transit: string;
  bank: string;
  account: string;
  text: string;
}

const extractDetails = (text: string): ExtractedDetails => {
  const result: ExtractedDetails = {
    transit: '',
    bank: '',
    account: '',
    text,
  };

  const matches = {
    transit: text.match(/[\\dbcd]*a(\d{5})d/),
    bank: text.match(/.*d(\d{3})a/),
    account: text.match(/[\\dbcd]*a([\dd]+)c/),
  };

  if (matches.transit?.[1]) result.transit = matches.transit[1];
  if (matches.bank?.[1]) result.bank = matches.bank[1];
  if (matches.account?.[1]) result.account = matches.account[1].replace(/d/g, '');

  return result;
};

const cropBottom = (source: HTMLCanvasElement | HTMLImageElement): HTMLCanvasElement | null => {
  const croppedHeight = source.height * 0.25;
  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = source.width;
  croppedCanvas.height = croppedHeight;

  const croppedContext = croppedCanvas.getContext('2d');
  croppedContext?.drawImage(
    source,
    0,
    source.height - croppedHeight,
    source.width,
    croppedHeight,
    0,
    0,
    source.width,
    croppedHeight,
  );

  return croppedCanvas;
};

const processImage = async (file: File): Promise<HTMLCanvasElement | null> => {
  const image = new Image();

  return new Promise(resolve => {
    image.onload = () => resolve(cropBottom(image));
    image.src = URL.createObjectURL(file);
  });
};

const fileToImage = async (file: File): Promise<Blob> => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas context not available');
  }

  return new Promise((resolve, reject) => {
    try {
      const processFile = async () => {
        const processedCanvas = file.type.startsWith('image/') ? await processImage(file) : null;

        if (!processedCanvas) throw new Error('Failed to process file');

        processedCanvas.toBlob(blob => {
          if (!blob) throw new Error('Failed to create blob');

          resolve(blob);
        });
      };

      processFile();
    } catch (error) {
      reject(error);
    }
  });
};

const Ocr: FunctionComponent = () => {
  const [originalImagePath, setOriginalImagePath] = useState<string>('');
  const [imagePath, setImagePath] = useState<string>('');
  const [details, setDetails] = useState<ExtractedDetails>({
    transit: '',
    bank: '',
    account: '',
    text: '',
  });

  const resetDetails = () => {
    setDetails({transit: '', bank: '', account: '', text: ''});
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    resetDetails();

    setOriginalImagePath('');
    if (file.type.startsWith('image')) {
      setOriginalImagePath(URL.createObjectURL(file));
    }

    // Crop file 25% bottom
    const image = await fileToImage(file);
    if (image) setImagePath(URL.createObjectURL(image));
  };

  const handleExtraction = async () => {
    const {createWorker, PSM} = await import('tesseract.js');

    try {
      resetDetails();

      const worker = await createWorker('mirc', 0, {langPath: '/ocr', gzip: false});
      await worker.setParameters({
        tessedit_pageseg_mode: PSM.AUTO,
      });

      const {
        data: {text},
      } = await worker.recognize(imagePath);
      const extractedDetails = extractDetails(text.replaceAll(/[\n\s]/g, ''));
      setDetails(extractedDetails);

      await worker.terminate();
    } catch (error) {
      logger.error('OCR extraction failed:', error);
    }
  };

  return (
    <div className="mx-auto mt-40 flex max-w-md rounded-lg bg-white p-4 shadow">
      <div className="w-full">
        <div className="relative mb-3 overflow-hidden border-2 border-black bg-black">
          {originalImagePath && (
            <img src={originalImagePath} alt="Original document" className="w-full opacity-50 blur-[1px]" />
          )}
          {imagePath && <img src={imagePath} alt="Selected document" className="absolute bottom-0 left-0 w-full" />}
        </div>

        <input type="file" onChange={handleFileChange} className="w-full rounded bg-gray-100 p-2 text-center" />

        <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1 font-mono text-sm">
          {Object.entries(details).map(([key, value]) => (
            <div key={key}>
              {key}:<strong>{value || '–'}</strong>
            </div>
          ))}
        </div>

        <button
          onClick={handleExtraction}
          className="mt-3 flex w-full items-center justify-center gap-1 rounded bg-violet-600 px-5 py-2 font-bold text-white transition-colors hover:bg-violet-700 disabled:pointer-events-none disabled:opacity-80"
          disabled={!imagePath}
        >
          Extract
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Ocr;
